// Added React and hook imports to fix reported errors
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Plus, Search, Heart, Trash2, Eye, EyeOff, 
  Settings, X, Save, Wand2, Eraser, Check, 
  Library, RotateCcw,
  PlusCircle, RefreshCw, Palette,
  ChevronUp, ChevronDown, ChevronRight, Undo2, 
  ArrowLeft, Moon, Sun, Copy, Users
} from 'lucide-react';
import { User as FirebaseUser } from 'firebase/auth';
import { ViewState, ReportTemplate, Phrase, Category, Filter, UserProfile } from '../types';
import { 
  subscribeToReports, subscribeToPhrases, subscribeToCategories, 
  savePhraseToCloud, saveReportToCloud, deleteCategoryFromCloud,
  saveCategoryToCloud, swapCategoriesOrder,
  restoreReportFromTrash, restorePhraseFromTrash, permanentDeleteReport, permanentDeletePhrase, emptyReportTrash, emptyPhraseTrash,
  moveReportToTrash, movePhraseToTrash,
  toggleFavoriteReportCloud, toggleFavoritePhraseCloud,
  subscribeToDirectory, sendPeerMessage,
  subscribeToUserProfile, subscribeToRegionalRestrictions
} from '../services/db';
import { generateReportContent } from '../services/geminiService';
import { copyHtmlToClipboard, copyToClipboard } from '../services/clipboardUtils';
import { sanitizeHtml } from '../utils/sanitize';
import { getRegionalAccess } from '../utils/regionalAccess';
import ReportCard from './ReportCard';
import PhraseCard from './PhraseCard';
import FilterAccordion from './FilterAccordion';
import Modal from './Modal';

// Helper para limpiar la salida de la IA antes de inyectar en el editor
const cleanAIOutputToHtml = (text: string) => {
  if (!text) return "";
  
  // 1. Normalizamos saltos de línea
  let processed = text.replace(/\r\n/g, "\n").replace(/&nbsp;/g, " ");
  
  // 2. Limpieza de indentaciones y caracteres prohibidos al inicio de cada línea
  // Preservamos líneas que parecen HTML (tablas)
  processed = processed.split("\n").map(line => {
    const trimmed = line.trim();
    if (trimmed.startsWith('<') || trimmed.endsWith('>')) {
      return line;
    }
    return line
      .trimStart() // Elimina sangrías/espacios al inicio
      .replace(/^[\s*•\-\d.]+\s*/, "") // Elimina viñetas, guiones, números de lista al inicio
      .replace(/^["'](.*)["']$/, "$1"); // Elimina comillas que envuelven la línea
  }).join("\n");

  // 3. Limpiar asteriscos y comillas residuales (solo fuera de etiquetas HTML)
  let cleaned = "";
  let inTag = false;
  for (let i = 0; i < processed.length; i++) {
    const char = processed[i];
    if (char === '<') inTag = true;
    if (!inTag) {
      if (char !== '*' && char !== '"') {
        cleaned += char;
      }
    } else {
      cleaned += char;
    }
    if (char === '>') inTag = false;
  }

  // 4. Convertir Markdown Bold a HTML
  let html = cleaned.replace(/(\*\*|__)(.*?)\1/g, '<b>$2</b>');
  
  // 5. Dividir por párrafos (doble salto de línea)
  const paragraphs = html.split(/\n\n+/);
  
  // Envolver cada párrafo en <p> y convertir saltos simples en <br>
  const formatted = paragraphs
    .map(p => {
      const trimmed = p.trim();
      // Si el párrafo es una tabla, lo devolvemos tal cual
      if (trimmed.startsWith('<table') || trimmed.startsWith('<thead') || trimmed.startsWith('<tbody') || trimmed.startsWith('<tr')) {
        return p;
      }
      return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
    })
    .join('');
    
  return formatted.trim();
};

interface PruebaScreenProps {
  user: FirebaseUser | null;
  view: ViewState;
  onNavigate: (view: ViewState) => void;
  onSelectForModular1: (report: ReportTemplate) => void;
  externalEditReportId?: string | null;
  onExternalEditConsumed?: () => void;
}

const AVAILABLE_COLORS = [
  'bg-blue-600', 'bg-red-500', 'bg-green-500', 'bg-purple-500', 
  'bg-orange-500', 'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-gray-500'
];

const PruebaScreen: React.FC<PruebaScreenProps> = ({ user, view: initialView, onNavigate, onSelectForModular1, externalEditReportId, onExternalEditConsumed }) => {
  const [userProfile, setUserProfile] = useState<any>(null);
  const [regionalRestrictions, setRegionalRestrictions] = useState<any[]>([]);

  useEffect(() => {
    if (!user?.uid) return;
    const unsubProfile = subscribeToUserProfile(user.uid, setUserProfile);
    const unsubRestrictions = subscribeToRegionalRestrictions(setRegionalRestrictions);
    return () => {
      unsubProfile();
      unsubRestrictions();
    };
  }, [user?.uid]);

  const activeRegionalSettings = useMemo(() => {
    if (!userProfile) return { renameConclusionToSummary: false, disableDiferenciales: false };
    const userEmail = (user?.email || '').toLowerCase();
    const isMaster = userEmail === 'dr.francisco.reyes@gmail.com' || user?.uid === 'guest_dev_offline';
    const isCurrentlyAdmin = isMaster || userProfile?.role === 'ADMIN';
    return getRegionalAccess(userProfile, regionalRestrictions, (isCurrentlyAdmin || !!userProfile?.internalTestingBypass));
  }, [userProfile, regionalRestrictions, user]);

  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const isModuleNarrow = containerWidth < 360;
  const isModuleVeryNarrow = containerWidth < 280;

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('module_theme_reports');
    if (saved) return saved === 'dark';
    const global = localStorage.getItem('app_theme') || 'mixed';
    return global === 'dark';
  });

  const [localView, setLocalView] = useState<ViewState>(initialView);
  useEffect(() => { setLocalView(initialView); }, [initialView]);

  const [reports, setReports] = useState<ReportTemplate[]>([]);
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeTab, setActiveTab] = useState<'REPORTS' | 'PHRASES'>('REPORTS');
  
  // Estados para Compartir
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [itemToShare, setItemToShare] = useState<{type: 'REPORT' | 'PHRASE', data: any} | null>(null);
  const [directory, setDirectory] = useState<UserProfile[]>([]);
  const [isSharing, setIsSharing] = useState(false);

  const [editingCategoryType, setEditingCategoryType] = useState<'REPORT' | 'PHRASE'>('REPORT');
  const [newCatName, setNewCatName] = useState('');
  const [newCatColor, setNewCatColor] = useState('bg-blue-600');

  const [addingFilterToCatId, setAddingFilterToCatId] = useState<string | null>(null);
  const [newFilterName, setNewFilterName] = useState('');
  const [deletingCatId, setDeletingCatId] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOriginModular1, setEditOriginModular1] = useState(false);
  const [aiTitle, setAiTitle] = useState('');
  const [newReportContent, setNewReportContent] = useState('');
  const [newReportFindingsInput, setNewReportFindingsInput] = useState('');
  const [selectedReportFilters, setSelectedReportFilters] = useState<string[]>([]);
  const [newPhraseText, setNewPhraseText] = useState('');
  const [selectedPhraseFilters, setSelectedPhraseFilters] = useState<string[]>([]);

  useEffect(() => {
    if (externalEditReportId && reports.length > 0) {
      const report = reports.find(r => r.id === externalEditReportId);
      if (report) {
        setEditingId(report.id);
        setAiTitle(report.title);
        setNewReportContent(report.content);
        setSelectedReportFilters(report.filterIds);
        setLocalView(ViewState.EDIT_REPORT);
        setEditOriginModular1(true);
        onExternalEditConsumed?.();
      }
    }
  }, [externalEditReportId, reports, onExternalEditConsumed]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [lastAutoCopiedId, setLastAutoCopiedId] = useState<string | null>(null);

  const [aiStyle, setAiStyle] = useState<'narrative' | 'structured'>(() => (localStorage.getItem('ai_pref_style') as 'narrative' | 'structured') || 'structured');
  const [aiExtension, setAiExtension] = useState<'Breve' | 'Medio' | 'Extenso'>(() => (localStorage.getItem('ai_pref_extension') as 'Breve' | 'Medio' | 'Extenso') || 'Medio');
  const [aiSeparation, setAiSeparation] = useState<'Párrafos' | 'Salto de Línea'>(() => (localStorage.getItem('ai_pref_separation') as 'Párrafos' | 'Salto de Línea') || 'Salto de Línea');
  const [aiAnatomical, setAiAnatomical] = useState(() => localStorage.getItem('ai_pref_anatomical') !== 'false');
  const [aiTechnique, setAiTechnique] = useState(() => localStorage.getItem('ai_pref_technique') === 'true');
  const [aiConclusion, setAiConclusion] = useState(() => localStorage.getItem('ai_pref_conclusion') !== 'false');
  const [aiDifferential, setAiDifferential] = useState(() => localStorage.getItem('ai_pref_differential') === 'true');

  useEffect(() => {
    if (activeRegionalSettings.disableDiferenciales) {
      setAiDifferential(false);
    }
  }, [activeRegionalSettings.disableDiferenciales]);

  useEffect(() => {
    localStorage.setItem('ai_pref_style', aiStyle);
    localStorage.setItem('ai_pref_extension', aiExtension);
    localStorage.setItem('ai_pref_separation', aiSeparation);
    localStorage.setItem('ai_pref_anatomical', String(aiAnatomical));
    localStorage.setItem('ai_pref_technique', String(aiTechnique));
    localStorage.setItem('ai_pref_conclusion', String(aiConclusion));
    localStorage.setItem('ai_pref_differential', String(aiDifferential));
  }, [aiStyle, aiExtension, aiSeparation, aiAnatomical, aiTechnique, aiConclusion, aiDifferential]);

  const [reportSearch, setReportSearch] = useState('');
  const [phraseSearch, setPhraseSearch] = useState('');
  
  const [showReportFilters, setShowReportFilters] = useState(() => localStorage.getItem('modular_show_report_filters') !== 'false');
  const [showPhraseFilters, setShowPhraseFilters] = useState(() => localStorage.getItem('modular_show_phrase_filters') !== 'false');
  
  const [filterPanelHeight, setFilterPanelHeight] = useState(() => {
    const saved = localStorage.getItem('modular_library_filter_height');
    return saved ? parseInt(saved) : 220;
  });

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = filterPanelHeight;
    
    document.body.style.cursor = 'row-resize';
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientY - startY;
      const newHeight = Math.max(100, Math.min(600, startHeight + delta));
      setFilterPanelHeight(newHeight);
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    localStorage.setItem('modular_library_filter_height', String(filterPanelHeight));
  }, [filterPanelHeight]);
  
  const [viewReportFilters, setViewReportFilters] = useState<string[]>([]);
  const [viewPhraseFilters, setViewPhraseFilters] = useState<string[]>([]);
  
  const [reportFavsOnly, setReportFavsOnly] = useState(false);
  const [phraseFavsOnly, setPhraseFavsOnly] = useState(false);
  
  const [autoClearReports, setAutoClearReports] = useState(() => localStorage.getItem('modular_auto_clear_reports') === 'true');
  const [autoClearPhrases, setAutoClearPhrases] = useState(() => localStorage.getItem('modular_auto_clear_phrases') === 'true');

  useEffect(() => {
    localStorage.setItem('modular_auto_clear_reports', String(autoClearReports));
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'SYNC_LIBRARY_SETTINGS', key: 'modular_auto_clear_reports', value: String(autoClearReports) });
    bc.close();
  }, [autoClearReports]);

  useEffect(() => {
    localStorage.setItem('modular_auto_clear_phrases', String(autoClearPhrases));
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.postMessage({ type: 'SYNC_LIBRARY_SETTINGS', key: 'modular_auto_clear_phrases', value: String(autoClearPhrases) });
    bc.close();
  }, [autoClearPhrases]);

  useEffect(() => {
    const handleSettingsChange = () => {
      const r = localStorage.getItem('modular_auto_clear_reports') === 'true';
      const p = localStorage.getItem('modular_auto_clear_phrases') === 'true';
      if (r !== autoClearReports) setAutoClearReports(r);
      if (p !== autoClearPhrases) setAutoClearPhrases(p);
    };
    window.addEventListener('app_settings_changed', handleSettingsChange);
    return () => window.removeEventListener('app_settings_changed', handleSettingsChange);
  }, [autoClearReports, autoClearPhrases]);

  useEffect(() => {
    if (!autoClearReports || !reportSearch) return;
    const timeout = setTimeout(() => { setReportSearch(''); }, 10000);
    return () => clearTimeout(timeout);
  }, [reportSearch, autoClearReports]);

  useEffect(() => {
    if (!autoClearPhrases || (!phraseSearch && viewPhraseFilters.length === 0)) return;
    const timeout = setTimeout(() => { setPhraseSearch(''); setViewPhraseFilters([]); }, 10000);
    return () => clearTimeout(timeout);
  }, [phraseSearch, viewPhraseFilters, autoClearPhrases]);

  useEffect(() => {
    if (!user) return;
    const unsubR = subscribeToReports(user.uid, setReports);
    const unsubP = subscribeToPhrases(user.uid, setPhrases);
    const unsubC = subscribeToCategories(user.uid, setCategories);
    return () => { unsubR(); unsubP(); unsubC(); };
  }, [user]);

  // Carga de directorio solo al abrir el modal de compartido
  useEffect(() => {
    if (isShareModalOpen && user) {
      const unsub = subscribeToDirectory(user.uid, (users) => {
        setDirectory(users.filter(u => u.uid !== user.uid));
      });
      return () => unsub();
    }
  }, [isShareModalOpen, user]);

  useEffect(() => {
    const handleThemeChange = () => {
       const savedLocal = localStorage.getItem('module_theme_reports');
       if (!savedLocal) {
         const global = localStorage.getItem('app_theme') || 'mixed';
         setIsDarkMode(global === 'dark');
       }
    };
    window.addEventListener('app_theme_changed', handleThemeChange);
    return () => window.removeEventListener('app_theme_changed', handleThemeChange);
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('module_theme_reports', next ? 'dark' : 'day');
    window.dispatchEvent(new Event('module_theme_changed'));
  };

  const resetForms = () => {
    setEditingId(null);
    setAiTitle('');
    setNewReportContent('');
    setNewReportFindingsInput('');
    setSelectedReportFilters([]);
    setNewPhraseText('');
    setSelectedPhraseFilters([]);
  };

  const handleShareClick = (item: any, type: 'REPORT' | 'PHRASE') => {
    setItemToShare({ type, data: item });
    setIsShareModalOpen(true);
  };

  const executeShare = async (targetUser: UserProfile) => {
    if (!user || !itemToShare || isSharing) return;
    setIsSharing(true);
    try {
      const chatId = [user.uid, targetUser.uid].sort().join('_');
      await sendPeerMessage(chatId, {
        id: `share_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
        senderId: user.uid,
        text: `Te comparto un recurso de mi biblioteca: ${itemToShare.type === 'REPORT' ? itemToShare.data.title : 'Frase Predefinida'}`,
        timestamp: Date.now(),
        sharedContent: {
          type: itemToShare.type,
          data: itemToShare.data,
          title: itemToShare.type === 'REPORT' ? itemToShare.data.title : 'Frase Compartida'
        }
      });
      alert(`Recurso compartido con ${targetUser.displayName} correctamente.`);
      setIsShareModalOpen(false);
    } catch (e) {
      console.error(e);
      alert("No se ha podido compartir el recurso en este momento.");
    } finally {
      setIsSharing(false);
    }
  };

  const handleGenerateAIReport = async () => {
    if (!aiTitle.trim()) return alert("Por favor, asigne un título al informe.");
    setIsGenerating(true);
    try {
      const result = await generateReportContent({
        title: aiTitle,
        clinicalData: newReportFindingsInput || "Generar estructura estándar normal.",
        styleMode: aiStyle,
        extension: aiExtension,
        anatomicalMode: aiAnatomical,
        separationType: aiSeparation,
        includeTechnique: aiTechnique,
        includeConclusion: aiConclusion,
        includeDifferential: aiDifferential,
        language: 'Español'
      });
      setNewReportContent(cleanAIOutputToHtml(result));
    } catch (e: any) { 
      alert("No se ha podido generar el informe. Por favor, intente de nuevo en unos instantes."); 
    } finally { setIsGenerating(false); }
  };

  const handleSaveReportFinal = async () => {
    if (!user || !aiTitle.trim() || !newReportContent.trim()) return alert("El título y el contenido son obligatorios para guardar.");
    const existing = reports.find(r => r.id === editingId);
    const sanitizedContent = sanitizeHtml(newReportContent);
    const reportData: ReportTemplate = {
      id: editingId || Date.now().toString(),
      title: aiTitle.toUpperCase(),
      content: sanitizedContent,
      filterIds: selectedReportFilters,
      isFavorite: existing?.isFavorite || false,
      usageCount: existing?.usageCount || 0,
      createdAt: existing?.createdAt || Date.now(),
      updatedAt: Date.now()
    };
    await saveReportToCloud(user.uid, reportData);
    if (editOriginModular1) {
      setEditOriginModular1(false);
      onNavigate(ViewState.MODULAR_1);
    } else {
      setLocalView(ViewState.REPORTS);
    }
    resetForms();
  };

  const handleSavePhraseFinal = async () => {
    if (!user || !newPhraseText.trim()) return alert("El contenido de la frase es obligatorio.");
    const existing = phrases.find(p => p.id === editingId);
    const sanitizedText = sanitizeHtml(newPhraseText);
    const phraseData: Phrase = {
      id: editingId || Date.now().toString(),
      text: sanitizedText,
      filterIds: selectedPhraseFilters,
      isFavorite: existing?.isFavorite || false,
      isFrequent: existing?.isFrequent || false,
      usageCount: existing?.usageCount || 0,
      createdAt: existing?.createdAt || Date.now()
    };
    await savePhraseToCloud(user.uid, phraseData);
    setLocalView(ViewState.PHRASES);
    resetForms();
  };

  const handleCreateCategory = async () => {
    if (!user || !newCatName.trim()) return;
    const newCat: Category = {
      id: `cat_${Date.now()}`,
      name: newCatName.toUpperCase(),
      color: newCatColor.replace('bg-', 'text-'),
      isVisible: true,
      type: editingCategoryType,
      filters: [],
      order: categories.filter(c => c.type === editingCategoryType).length
    };
    await saveCategoryToCloud(user.uid, newCat);
    setNewCatName('');
  };

  const confirmAddFilter = async (catId: string) => {
    if (!newFilterName.trim() || !user) return;
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    const newFilter: Filter = { id: `f_${Date.now()}`, name: newFilterName.toUpperCase(), isActive: true };
    const updatedCat = { ...cat, filters: [...cat.filters, newFilter] };
    await saveCategoryToCloud(user.uid, updatedCat);
    setAddingFilterToCatId(null);
    setNewFilterName('');
  };

  const handleCycleCategoryColor = async (catId: string) => {
    if (!user) return;
    const cat = categories.find(c => c.id === catId);
    if (!cat) return;
    const currentColorBg = cat.color.replace('text-', 'bg-');
    let currentIndex = AVAILABLE_COLORS.indexOf(currentColorBg);
    if (currentIndex === -1) {
       const colorName = cat.color.split('-')[1];
       currentIndex = AVAILABLE_COLORS.findIndex(c => c.includes(colorName));
    }
    const nextIndex = (currentIndex + 1) % AVAILABLE_COLORS.length;
    const nextColor = AVAILABLE_COLORS[nextIndex].replace('bg-', 'text-');
    const updatedCat = { ...cat, color: nextColor };
    await saveCategoryToCloud(user.uid, updatedCat);
  };

  const handleMoveCategory = async (catId: string, dir: 'UP' | 'DOWN') => {
    if (!user) return;
    
    const typeCats = categories
      .filter(c => c.type === editingCategoryType)
      .sort((a,b) => (a.order ?? 0) - (b.order ?? 0));
    
    const idx = typeCats.findIndex(c => c.id === catId);
    if (idx === -1) return;
    
    const targetIdx = dir === 'UP' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= typeCats.length) return;
    
    const catA = { ...typeCats[idx], order: typeCats[idx].order ?? idx };
    const RecordB = { ...typeCats[targetIdx], order: typeCats[targetIdx].order ?? targetIdx };
    
    await swapCategoriesOrder(user.uid, catA, RecordB);
  };

  const filteredReports = useMemo(() => {
    let res = reports.filter(r => !r.deletedAt);
    if (reportSearch) res = res.filter(r => r.title.toLowerCase().includes(reportSearch.toLowerCase()));
    else {
       if (reportFavsOnly) res = res.filter(r => r.isFavorite);
       if (viewReportFilters.length > 0) res = res.filter(r => viewReportFilters.every(fid => r.filterIds.includes(fid)));
    }
    return res.sort((a,b) => (b.usageCount || 0) - (a.usageCount || 0));
  }, [reports, reportSearch, viewReportFilters, reportFavsOnly]);

  const filteredPhrases = useMemo(() => {
    let res = phrases.filter(p => !p.deletedAt);
    if (phraseSearch) res = res.filter(p => p.text.toLowerCase().includes(phraseSearch.toLowerCase()));
    else {
       if (phraseFavsOnly) res = res.filter(p => p.isFavorite);
       if (viewPhraseFilters.length > 0) res = res.filter(p => viewPhraseFilters.every(fid => p.filterIds.includes(fid)));
    }
    return res.sort((a,b) => (b.usageCount || 0) - (a.usageCount || 0));
  }, [phrases, phraseSearch, viewPhraseFilters, phraseFavsOnly]);

  // Efecto para copiado automático cuando solo queda 1 frase al filtrar
  useEffect(() => {
    if (activeTab !== 'PHRASES' || filteredPhrases.length !== 1) {
      if (filteredPhrases.length === 0 || filteredPhrases.length > 1) {
        setLastAutoCopiedId(null);
      }
      return;
    }

    const hasActiveFilters = phraseSearch.trim().length > 0 || viewPhraseFilters.length > 0;
    const phrase = filteredPhrases[0];

    if (hasActiveFilters && lastAutoCopiedId !== phrase.id) {
      const performAutoCopy = async () => {
        const useRichText = localStorage.getItem('modular_copy_rich_text') !== 'false';
        let success = false;
        if (useRichText) {
          success = await copyHtmlToClipboard(phrase.text);
        } else {
          success = await copyToClipboard(phrase.text);
        }
        if (success) {
          setLastAutoCopiedId(phrase.id);
        }
      };
      performAutoCopy();
    }
  }, [filteredPhrases, activeTab, phraseSearch, viewPhraseFilters, lastAutoCopiedId]);

  const FilterSelector = ({ selected, setSelected, type }: { selected: string[], setSelected: (ids: string[]) => void, type: 'REPORT' | 'PHRASE' }) => {
    const toggle = (id: string) => setSelected(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
    const relevantCategories = categories.filter(c => c.type === type && c.isVisible).sort((a,b) => (a.order ?? 0) - (b.order ?? 0));
    return (
        <div className="space-y-3 mt-4">
            <label className={`text-[10px] font-black uppercase tracking-wider block border-b pb-1 ${isDarkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-100'}`}>Etiquetas</label>
            {relevantCategories.map(cat => (
                <div key={cat.id} className={`rounded-xl border p-2 shadow-sm ${isDarkMode ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-100'}`}>
                    <div className="flex items-center space-x-1.5 mb-1.5">
                      <div className={`w-2 h-2 rounded-full ${cat.color.replace('text-', 'bg-')}`}></div>
                      <span className={`text-[10px] font-black uppercase tracking-tight ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{cat.name}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.filters.map(filter => { 
                        const isSelected = selected.includes(filter.id); 
                        const colorPart = cat.color.split('-')[1] || 'blue';
                        const activeClass = `bg-${colorPart}-600 border-${colorPart}-600 text-white`; 
                        return (
                          <button key={filter.id} onClick={() => toggle(filter.id)} className={`px-2 py-1 text-[9px] rounded border font-black uppercase tracking-widest transition-all ${isSelected ? activeClass : (isDarkMode ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-white text-gray-500 border-gray-200')}`}>
                            {filter.name}
                          </button>
                        ); 
                      })}
                    </div>
                </div>
            ))}
        </div>
    );
  };

  const renderManageCategories = () => (
    <div className={`flex flex-col h-full transition-colors ${isDarkMode ? 'bg-[#1e2024]' : 'bg-[#fcfdfe]'}`}>
       <div className={`px-4 py-2.5 border-b flex justify-between items-center ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center space-x-2">
             <button onClick={() => setLocalView(activeTab === 'REPORTS' ? ViewState.REPORTS : ViewState.PHRASES)} className="p-1 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-all">
                <ArrowLeft size={16} className="text-slate-400" />
             </button>
             <h1 className={`font-['Outfit'] font-bold text-[13px] tracking-tight uppercase ${isDarkMode ? 'text-white' : 'text-black'}`}>Categorías</h1>
          </div>
          <div className="flex items-center space-x-2">
             <button onClick={toggleDarkMode} className={`p-1.5 rounded-lg border transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-yellow-400' : 'bg-white border-slate-200 text-slate-400'}`}>
                {isDarkMode ? <Sun size={12} /> : <Moon size={12} />}
             </button>
             <button onClick={() => setEditingCategoryType(editingCategoryType === 'REPORT' ? 'PHRASE' : 'REPORT')} className="px-3 py-1 bg-[#2563eb] text-white rounded-full text-[8px] font-black uppercase shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all tracking-[0.1em]">{editingCategoryType === 'REPORT' ? 'Informes' : 'Frases'}</button>
          </div>
       </div>
       <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
          <div className={`p-4 rounded-[20px] border shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-slate-100'}`}>
             <div className="flex items-center space-x-2 mb-3">
                <PlusCircle size={14} className="text-blue-600" />
                <label className={`text-[9px] font-black uppercase tracking-widest block ml-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Nueva ({editingCategoryType === 'REPORT' ? 'Informe' : 'Frase'})
                </label>
             </div>
             <input type="text" value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Nombre..." className={`w-full p-2.5 rounded-xl border text-[12px] font-black mb-4 outline-none transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-white' : 'bg-white border-slate-200 text-slate-800'}`} />
             <div className="flex flex-col gap-3">
                <div className={`flex flex-wrap items-center justify-center gap-2 p-2.5 rounded-xl border ${isDarkMode ? 'bg-black/20 border-gray-700' : 'bg-gray-50 border-slate-100'}`}>
                   {AVAILABLE_COLORS.map(c => <button key={c} onClick={() => setNewCatColor(c)} className={`w-4 h-4 rounded-full transition-all ${c} ${newCatColor === c ? 'ring-2 ring-offset-2 ring-blue-500 scale-110 shadow-lg' : 'opacity-40 hover:opacity-100'}`} />)}
                </div>
                <button onClick={handleCreateCategory} className="w-full py-3 bg-[#2563eb] text-white rounded-xl text-[9px] font-black uppercase flex items-center justify-center space-x-2 shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition-all active:scale-95"><PlusCircle size={12}/> <span>Añadir Categoría</span></button>
             </div>
          </div>
          <div className="space-y-4">
             {categories.filter(c => c.type === editingCategoryType).sort((a,b) => (a.order ?? 0) - (b.order ?? 0)).map(cat => (
               <div key={cat.id} className={`p-3.5 rounded-[16px] border shadow-md flex flex-col transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-slate-100'}`}>
                  <div className="flex justify-between items-center mb-3">
                     <div className="flex items-center space-x-3">
                        <div className="flex flex-col -space-y-1 text-gray-300">
                           <button onClick={() => handleMoveCategory(cat.id, 'UP')} className="hover:text-blue-500 transition-colors"><ChevronUp size={16}/></button>
                           <button onClick={() => handleMoveCategory(cat.id, 'DOWN')} className="hover:text-blue-500 transition-colors"><ChevronDown size={16}/></button>
                        </div>
                        <div className={`w-3 h-3 rounded-full shrink-0 ${cat.color.replace('text-', 'bg-')}`}></div>
                        <h3 className={`font-['Outfit'] font-black text-[11px] uppercase tracking-[0.1em] ${isDarkMode ? 'text-white' : 'text-black'}`}>{cat.name}</h3>
                     </div>
                     <div className="flex items-center space-x-1.5">
                        <button onClick={() => handleCycleCategoryColor(cat.id)} className="p-1.5 text-gray-400 hover:text-blue-500 rounded-lg transition-all"><Palette size={14}/></button>
                        {addingFilterToCatId === cat.id ? (
                           <div className={`flex items-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border border-indigo-500 rounded-xl px-2 py-1 animate-in slide-in-from-right-2 ml-1 shadow-xl`}>
                              <input autoFocus type="text" value={newFilterName} onChange={e => setNewFilterName(e.target.value)} onKeyDown={e => e.key === 'Enter' && confirmAddFilter(cat.id)} placeholder="Nombre..." className={`${isDarkMode ? 'bg-transparent text-white' : 'bg-white text-gray-900'} outline-none text-[11px] font-black w-24 border-none focus:ring-0`} />
                              <button onClick={() => confirmAddFilter(cat.id)} className="text-indigo-600 ml-1.5 p-1"><Check size={14}/></button>
                              <button onClick={() => setAddingFilterToCatId(null)} className="text-gray-400 ml-1 p-1"><X size={14}/></button>
                           </div>
                        ) : (
                           <button onClick={() => setAddingFilterToCatId(cat.id)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg transition-all"><Plus size={16}/></button>
                        )}
                        <div className="h-5 w-[1px] bg-slate-200 dark:bg-slate-700 mx-1"></div>
                        {deletingCatId === cat.id ? (
                           <div className="flex items-center space-x-2 animate-in zoom-in-95 bg-white border border-red-500 rounded-xl px-2 py-1 shadow-xl">
                              <button onClick={async () => { await deleteCategoryFromCloud(user!.uid, cat.id); setDeletingCatId(null); }} className="px-3 py-1 bg-red-600 text-white rounded-lg text-[9px] font-black uppercase">BORRAR</button>
                              <button onClick={() => setDeletingCatId(null)} className="p-1 text-gray-400"><X size={14}/></button>
                           </div>
                        ) : (
                           <button onClick={() => setDeletingCatId(cat.id)} className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg transition-all"><Trash2 size={14}/></button>
                        )}
                     </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                     {cat.filters.map(f => (
                       <div key={f.id} className={`px-2.5 py-1 rounded-lg border flex items-center space-x-2 shadow-sm transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-slate-100 text-slate-700'}`}>
                          <span className="text-[10px] font-black tracking-widest uppercase">{f.name}</span>
                          <X size={10} className="cursor-pointer text-gray-300 hover:text-red-500" onClick={async (e) => { e.stopPropagation(); const updated = { ...cat, filters: cat.filters.filter(it => it.id !== f.id) }; await saveCategoryToCloud(user!.uid, updated); }} />
                       </div>
                     ))}
                  </div>
               </div>
             ))}
          </div>
       </div>
    </div>
  );

  const renderCreateIA = () => {
    const handleCopyRichText = async () => {
       const useRichText = localStorage.getItem('modular_copy_rich_text') !== 'false';
       let success = false;
       if (useRichText) {
          success = await copyHtmlToClipboard(newReportContent);
       } else {
          const plainText = newReportContent
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n\n')
            .replace(/<\/div>/gi, '\n')
            .replace(/<[^>]*>?/gm, '')
            .replace(/&nbsp;/g, ' ')
            .trim();
          success = await copyToClipboard(plainText);
       }
       if (success) {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
       }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      
      const html = e.clipboardData.getData('text/html');
      const text = e.clipboardData.getData('text/plain');
      
      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Función recursiva para limpiar nodos
        const cleanNode = (node: Node): Node | null => {
          if (node.nodeType === Node.TEXT_NODE) {
            return node.cloneNode(true);
          }
          
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            const tag = el.tagName.toLowerCase();
            
            // Etiquetas permitidas
            const allowedTags = ['b', 'strong', 'i', 'em', 'u', 'p', 'br', 'table', 'tr', 'td', 'th', 'thead', 'tbody'];
            
            if (allowedTags.includes(tag)) {
              const newEl = document.createElement(tag);
              
              // Copiar el contenido recursivamente
              el.childNodes.forEach(child => {
                const cleaned = cleanNode(child);
                if (cleaned) newEl.appendChild(cleaned);
              });
              
              return newEl;
            } else {
              // Si no es permitida, intentamos extraer su contenido de texto o sus hijos
              const fragment = document.createDocumentFragment();
              el.childNodes.forEach(child => {
                const cleaned = cleanNode(child);
                if (cleaned) fragment.appendChild(cleaned);
              });
              return fragment;
            }
          }
          return null;
        };
        
        const fragment = document.createDocumentFragment();
        doc.body.childNodes.forEach(child => {
          const cleaned = cleanNode(child);
          if (cleaned) fragment.appendChild(cleaned);
        });
        
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(fragment);
        document.execCommand('insertHTML', false, tempDiv.innerHTML);
      } else if (text) {
        // Si es texto plano, convertimos saltos de línea a <br> o <p>
        const formattedText = text
          .replace(/\r\n/g, '\n')
          .split('\n\n')
          .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
          .join('');
        document.execCommand('insertHTML', false, formattedText);
      }
    };

    const execEditorCommand = (command: string, value: string | undefined = undefined) => {
      document.execCommand(command, false, value);
      const editor = document.querySelector('.report-viewer');
      if (editor) {
        setNewReportContent(editor.innerHTML);
      }
    };

    return (
      <div className={`flex flex-col h-full transition-colors ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
         <style>{`
            .report-viewer b, .report-viewer strong { font-weight: 800; color: inherit; }
            .report-viewer { font-family: 'Inter', sans-serif; line-height: 1.6; min-height: 400px; }
            .report-viewer p { margin-bottom: 0.75rem; }
            .report-viewer p:last-child { margin-bottom: 0; }
            .editor-toolbar button { transition: all 0.2s; }
            .editor-toolbar button:active { scale: 0.95; }
         `}</style>
         <div className={`px-6 py-4 border-b flex justify-between items-center sticky top-0 z-20 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center space-x-3"><div className="bg-blue-600 p-2 rounded-xl shadow-lg"><Wand2 size={18} className="text-white" /></div><h1 className={`font-['Outfit'] font-medium text-[18px] uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-black'}`}>{editingId ? 'Editar Informe' : 'Crear Informe IA'}</h1></div>
            <div className="flex items-center space-x-2">
               <button onClick={resetForms} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center space-x-2 transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}><RotateCcw size={14}/> <span>Nuevo</span></button>
               <button onClick={handleSaveReportFinal} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase shadow-lg shadow-blue-600/30 active:scale-95 transition-all">Guardar</button>
               <button onClick={() => setLocalView(ViewState.REPORTS)} className="p-2 text-gray-400 hover:bg-slate-100 rounded-xl transition-all"><X size={22}/></button>
            </div>
         </div>
         <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
            <div className={`px-4 py-3 rounded-2xl border shadow-sm flex items-center space-x-3 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex-1"><label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Título del Informe (Para la Biblioteca)</label><input type="text" value={aiTitle} onChange={(e) => setAiTitle(e.target.value.toUpperCase())} placeholder="EJ: TC DE TÓRAX CON CONTRASTE" className={`w-full text-lg font-black placeholder-gray-300 outline-none bg-transparent ${isDarkMode ? 'text-white' : 'text-slate-950 opacity-100'}`} /></div>
            </div>
            <div className={`p-5 rounded-[32px] border shadow-sm ${isDarkMode ? 'bg-blue-900/10 border-blue-900/20' : 'bg-blue-50/50 border-blue-100'}`}>
              <div className="flex items-center space-x-2 mb-3"><Wand2 size={12} className="text-blue-500" /><span className="text-[9px] font-black text-blue-700 uppercase tracking-widest">Configuración de Generación</span></div>
              
              <div className={`flex flex-wrap gap-x-2 gap-y-3 mb-5`}>
                <div className="flex-1 min-w-[100px]"><label className="text-[7px] font-black text-gray-500 uppercase tracking-widest block mb-1.5 ml-1">Estilo</label><div className={`flex ${isModuleNarrow ? 'flex-col' : 'flex-row'} rounded-xl border p-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}><button onClick={() => { setAiStyle('narrative'); setAiAnatomical(false); }} className={`flex-1 py-1 text-[8px] font-black uppercase rounded-lg transition-all ${aiStyle === 'narrative' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-50/10'}`}>Narrativo</button><button onClick={() => setAiStyle('structured')} className={`flex-1 py-1 text-[8px] font-black uppercase rounded-lg transition-all ${aiStyle === 'structured' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-50/10'}`}>Estruct.</button></div></div>
                <div className="flex-1 min-w-[100px]"><label className="text-[7px] font-black text-gray-500 uppercase tracking-widest block mb-1.5 ml-1">Extensión</label><div className={`flex ${isModuleNarrow ? 'flex-col' : 'flex-row'} rounded-xl border p-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>{(['Breve', 'Medio', 'Extenso'] as const).map(opt => (<button key={opt} onClick={() => setAiExtension(opt)} className={`flex-1 py-1 text-[8px] font-black uppercase rounded-lg transition-all ${aiExtension === opt ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-50/10'}`}>{opt}</button>))}</div></div>
                <div className="flex-1 min-w-[100px]"><label className="text-[7px] font-black text-gray-500 uppercase tracking-widest block mb-1.5 ml-1">Separación</label><div className={`flex ${isModuleNarrow ? 'flex-col' : 'flex-row'} rounded-xl border p-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}><button onClick={() => setAiSeparation('Párrafos')} className={`flex-1 py-1 text-[8px] font-black uppercase rounded-lg transition-all ${aiSeparation === 'Párrafos' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-50/10'}`}>Párrafos</button><button onClick={() => setAiSeparation('Salto de Línea')} className={`flex-1 py-1 text-[8px] font-black uppercase rounded-lg transition-all ${aiSeparation === 'Salto de Línea' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-400 hover:bg-gray-50/10'}`}>Líneas</button></div></div>
              </div>

              <div className={`grid ${isModuleVeryNarrow ? 'grid-cols-1' : 'grid-cols-2'} sm:flex sm:flex-wrap gap-1.5`}>
                {aiStyle === 'structured' && (
                  <button onClick={() => setAiAnatomical(!aiAnatomical)} className={`flex items-center justify-center sm:justify-start space-x-1 px-2 py-1 rounded-lg border text-[8px] font-black uppercase transition-all ${aiAnatomical ? 'bg-teal-500 text-white border-teal-600 shadow-sm' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{aiAnatomical && <Check size={8} />} <span>Anatómico</span></button>
                )}
                <button onClick={() => setAiTechnique(!aiTechnique)} className={`flex items-center justify-center sm:justify-start space-x-1 px-2 py-1 rounded-lg border text-[8px] font-black uppercase transition-all ${aiTechnique ? 'bg-indigo-50 text-white border-indigo-600 shadow-sm' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{aiTechnique && <Check size={8} />} <span>Técnica</span></button>
                <button onClick={() => setAiConclusion(!aiConclusion)} className={`flex items-center justify-center sm:justify-start space-x-1 px-2 py-1 rounded-lg border text-[8px] font-black uppercase transition-all ${aiConclusion ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{aiConclusion && <Check size={8} />} <span>{activeRegionalSettings.renameConclusionToSummary ? 'Resumen hallazgos' : 'Conclusión'}</span></button>
{!activeRegionalSettings.disableDiferenciales && (
                  <button onClick={() => setAiDifferential(!aiDifferential)} className={`flex items-center justify-center sm:justify-start space-x-1 px-2 py-1 rounded-lg border text-[8px] font-black uppercase transition-all ${aiDifferential ? 'bg-purple-500 text-white border-purple-600 shadow-sm' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{aiDifferential && <Check size={8} />} <span>Diferencial</span></button>
                )}
              </div>
              <div className="mt-5"><textarea value={newReportFindingsInput} onChange={(e) => setNewReportFindingsInput(e.target.value)} placeholder="Introduce hallazgos clínicos..." className={`w-full h-20 p-3 text-xs rounded-xl focus:ring-1 outline-none resize-none transition-all font-medium ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white focus:ring-blue-900' : 'bg-white border-gray-200 text-gray-800 focus:ring-blue-100'}`} /><button onClick={handleGenerateAIReport} disabled={isGenerating} className="w-full mt-2.5 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-[10px] font-black uppercase shadow-lg shadow-blue-600/20 transition-all flex justify-center items-center active:scale-95">{isGenerating ? (<div className="flex items-center"><RefreshCw size={12} className="animate-spin mr-1.5"/> Generando...</div>) : (<><Wand2 size={14} className="mr-1.5"/> Generar Informe Final</>)}</button></div>
            </div>
            <div className={`p-6 rounded-[32px] border shadow-sm flex flex-col min-h-[400px] ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
               <div className="flex justify-between items-center mb-4">
                  <label className={`text-[10px] font-black px-3 py-1 rounded-full uppercase block w-fit ${isDarkMode ? 'text-gray-300 bg-gray-800' : 'text-blue-600 bg-blue-50'}`}>Cuerpo del Informe</label>
                  <div className="flex items-center gap-4">
                    {newReportContent && (
                      <button onClick={handleCopyRichText} className={`flex items-center space-x-1 text-[9px] font-black uppercase transition-all ${copySuccess ? 'text-green-500' : 'text-gray-400 hover:text-blue-600'}`}>
                        {copySuccess ? <Check size={12} /> : <Copy size={12} />}
                        <span>{copySuccess ? '¡Listo para PACS!' : (localStorage.getItem('modular_copy_rich_text') !== 'false' ? 'Copiar con Formato' : 'Copiar Texto Plano')}</span>
                      </button>
                    )}
                  </div>
               </div>

               {/* BARRA DE HERRAMIENTAS DE TEXTO ENRIQUECIDO */}
               <div className={`editor-toolbar flex items-center flex-wrap gap-1 mb-4 p-2 rounded-2xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                  <button onClick={() => execEditorCommand('bold')} className={`p-2 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} title="Negrita"><b>B</b></button>
                  <button onClick={() => execEditorCommand('italic')} className={`p-2 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 ${isDarkMode ? 'text-gray-400' : 'text-gray-500 italic'}`} title="Cursiva">I</button>
                  <button onClick={() => execEditorCommand('underline')} className={`p-2 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 ${isDarkMode ? 'text-gray-400 underline' : 'text-gray-500 underline'}`} title="Subrayado">U</button>
                  <div className="w-[1px] h-4 bg-gray-300 dark:bg-gray-700 mx-1" />
                  <button onClick={() => execEditorCommand('formatBlock', '<p>')} className={`p-2 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 text-[10px] font-black uppercase ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} title="Párrafo">P</button>
                  <button onClick={() => execEditorCommand('insertUnorderedList')} className={`p-2 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 ${isDarkMode ? 'text-gray-400 font-black' : 'text-gray-500 font-black'}`} title="Lista">• List</button>
                  <div className="w-[1px] h-4 bg-gray-300 dark:bg-gray-700 mx-1" />
                  <button onClick={() => execEditorCommand('removeFormat')} className={`p-2 rounded-lg hover:bg-red-500/10 hover:text-red-500 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} title="Limpiar Formato"><Eraser size={14} /></button>
               </div>

               <div 
                 contentEditable 
                 onBlur={(e) => setNewReportContent(e.currentTarget.innerHTML)}
                 onPaste={handlePaste}
                 dangerouslySetInnerHTML={{ __html: sanitizeHtml(newReportContent) }}
                 className={`flex-1 w-full p-6 rounded-2xl text-[15px] report-viewer outline-none transition-all min-h-[300px] ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-100 focus:ring-1 focus:ring-blue-900' : 'bg-white border-gray-100 text-gray-900 focus:ring-1 focus:ring-blue-100'}`} 
               />
               <FilterSelector selected={selectedReportFilters} setSelected={setSelectedReportFilters} type="REPORT" />
            </div>
            <div className="pt-2 pb-10"><button onClick={handleSaveReportFinal} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 rounded-[28px] font-black uppercase text-sm shadow-2xl shadow-blue-600/30 transition-all flex items-center justify-center space-x-3 active:scale-95"><Save size={22} /><span>{editingId ? 'Actualizar Plantilla' : 'Añadir a la Biblioteca'}</span></button></div>
         </div>
      </div>
    );
  };

  const renderCreatePhrase = () => (
    <div className={`flex flex-col h-full transition-colors ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
       <div className={`px-6 py-4 border-b flex justify-between items-center ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}><div className="flex items-center space-x-3"><div className="bg-purple-600 p-2 rounded-xl shadow-lg shadow-purple-600/20"><Library size={18} className="text-white" /></div><h1 className={`font-['Outfit'] font-medium text-[18px] uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-black'}`}>Crear Frase Predefinida</h1></div><button onClick={() => setLocalView(ViewState.PHRASES)} className="p-2 text-gray-400 hover:bg-slate-100 rounded-xl transition-all"><X size={22}/></button></div>
       <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div><label className="text-[10px] font-medium text-black uppercase tracking-widest block mb-3 ml-1">Contenido de la Frase</label><textarea value={newPhraseText} onChange={e => setNewPhraseText(e.target.value)} placeholder="Ej: Estudio realizado con técnica adecuada y sin incidencias..." className={`w-full min-h-[150px] p-8 rounded-[36px] border text-lg font-medium shadow-sm outline-none resize-none transition-all focus:ring-2 focus:ring-purple-500/10 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-white' : 'bg-white border-gray-100 text-gray-900 placeholder-slate-400'}`} /></div>
          <FilterSelector selected={selectedPhraseFilters} setSelected={setSelectedPhraseFilters} type="PHRASE" />
          <button onClick={handleSavePhraseFinal} className="w-full py-4 bg-purple-600 text-white rounded-full font-black text-[12px] uppercase shadow-xl shadow-purple-600/20 active:scale-95 transition-all">Registrar Frase en Biblioteca</button>
       </div>
    </div>
  );

  const renderRecycleBin = () => {
    const deletedReports = reports.filter(r => !!r.deletedAt);
    const deletedPhrases = phrases.filter(p => !!p.deletedAt);
    const totalDeleted = deletedReports.length + deletedPhrases.length;

    return (
      <div className={`flex flex-col h-full transition-colors ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
        {/* HEADER SEGÚN SCREENSHOT */}
        <div className={`px-6 py-5 border-b flex justify-between items-center sticky top-0 z-20 shadow-md ${isDarkMode ? 'bg-[#2a2d34] border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center space-x-4">
            <button 
               onClick={() => setLocalView(activeTab === 'REPORTS' ? ViewState.REPORTS : ViewState.PHRASES)} 
               className={`p-1 rounded-lg transition-all ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
               <ArrowLeft size={24} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
            </button>
            <div className="bg-red-600 p-2 rounded-[14px] text-white shadow-xl shadow-red-900/20">
               <Trash2 size={20} />
            </div>
            <div className="flex flex-col leading-none">
               <h1 className={`font-['Outfit'] font-black text-[14px] uppercase tracking-[0.05em] ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Elementos</h1>
               <h1 className={`font-['Outfit'] font-black text-[14px] uppercase tracking-[0.05em] ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Eliminados</h1>
            </div>
          </div>
          
          <button 
            disabled={totalDeleted === 0}
            onClick={async () => { 
              if(confirm("¿Vaciar papelera permanentemente?")) { 
                await emptyReportTrash(user!.uid); 
                await emptyPhraseTrash(user!.uid); 
              } 
            }} 
            className={`px-4 py-3 bg-red-600 text-white rounded-[14px] font-black uppercase text-[10px] leading-tight shadow-2xl shadow-red-900/40 active:scale-95 transition-all flex flex-col items-center justify-center min-w-[100px] disabled:opacity-30`}
          >
            <span>VACIAR</span>
            <span>TODO</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-8 custom-scrollbar">
           {deletedReports.length > 0 && (
             <div className="space-y-4">
               <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-2 flex items-center gap-2">
                 <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div> Informes Eliminados
               </p>
               {deletedReports.map(r => (
                 <div key={r.id} className={`p-4 rounded-2xl border flex justify-between items-center shadow-sm animate-in fade-in slide-in-from-left-2 ${isDarkMode ? 'bg-[#2a2d34] border-gray-800' : 'bg-white border-gray-200'}`}>
                   <div className="min-w-0 pr-4">
                     <p className={`font-black text-xs uppercase truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{r.title}</p>
                     <p className="text-[9px] text-gray-500 font-medium">Borrado el {new Date(r.deletedAt!).toLocaleDateString()}</p>
                   </div>
                   <div className="flex items-center space-x-2">
                     <button onClick={() => restoreReportFromTrash(user!.uid, r.id)} className="p-2 text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-all" title="Restaurar"><Undo2 size={18}/></button>
                     <button onClick={() => permanentDeleteReport(user!.uid, r.id)} className="p-2 text-red-500 hover:bg-red-50/10 rounded-lg transition-all" title="Eliminar permanentemente"><X size={18}/></button>
                   </div>
                 </div>
               ))}
             </div>
           )}

           {deletedPhrases.length > 0 && (
             <div className="space-y-4">
               <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-2 flex items-center gap-2">
                 <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div> Frases Eliminadas
               </p>
               {deletedPhrases.map(p => (
                 <div key={p.id} className={`p-4 rounded-2xl border flex justify-between items-center shadow-sm animate-in fade-in slide-in-from-left-2 ${isDarkMode ? 'bg-[#2a2d34] border-gray-800' : 'bg-white border-gray-200'}`}>
                   <div className="min-w-0 pr-4">
                     <p className={`font-medium text-xs line-clamp-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{p.text}</p>
                     <p className="text-[9px] text-gray-500 font-medium">Borrado el {new Date(p.deletedAt!).toLocaleDateString()}</p>
                   </div>
                   <div className="flex items-center space-x-2">
                     <button onClick={() => restorePhraseFromTrash(user!.uid, p.id)} className="p-2 text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-all" title="Restaurar"><Undo2 size={18}/></button>
                     <button onClick={() => permanentDeletePhrase(user!.uid, p.id)} className="p-2 text-red-500 hover:bg-red-50/10 rounded-lg transition-all" title="Eliminar permanentemente"><X size={18}/></button>
                   </div>
                 </div>
               ))}
             </div>
           )}

           {deletedReports.length === 0 && deletedPhrases.length === 0 && (
             <div className="h-full flex flex-col items-center justify-center text-gray-600 opacity-20 py-20">
               <Trash2 size={64} className="mb-4" />
               <p className="font-black text-[10px] uppercase tracking-widest">Papelera Vacía</p>
             </div>
           )}
        </div>
      </div>
    );
  };

  const renderMainLibrary = () => (
    <div className={`flex flex-col h-full transition-colors ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
        <div className={`px-6 py-4 border-b flex justify-between items-center sticky top-0 z-20 shadow-sm ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
             <div className="flex items-center space-x-3"><div className={`${activeTab === 'REPORTS' ? 'bg-blue-600' : 'bg-purple-600'} p-2 rounded-xl shadow-sm`}><Library size={18} className="text-white" /></div><h1 className={`font-['Outfit'] font-medium text-xl tracking-tight uppercase ${isDarkMode ? 'text-white' : 'text-black'}`}>Biblioteca Médica</h1></div>
             <button onClick={toggleDarkMode} className={`p-2 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-yellow-400' : 'bg-white border-gray-200 text-gray-400'}`}>{isDarkMode ? <Sun size={18}/> : <Moon size={18}/>}</button>
        </div>
        <div className={`flex border-b transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
            <button onClick={() => setActiveTab('REPORTS')} className={`flex-1 py-3 text-[11px] font-black tracking-widest border-b-2 transition-all ${activeTab === 'REPORTS' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400'}`}>INFORMES</button>
            <button onClick={() => setActiveTab('PHRASES')} className={`flex-1 py-3 text-[11px] font-black tracking-widest border-b-2 transition-all ${activeTab === 'PHRASES' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-400'}`}>FRASES</button>
        </div>
        <div className={`border-b px-4 py-3 space-y-3 transition-colors ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
             <div className="flex justify-end items-center space-x-2">
                <div className="flex items-center space-x-1 border-r pr-3 border-gray-200 dark:border-gray-700">
                  <button onClick={() => { if (activeTab === 'REPORTS') { if (!reportFavsOnly) { setReportSearch(''); setViewReportFilters([]); } setReportFavsOnly(!reportFavsOnly); } else { if (!phraseFavsOnly) { setPhraseSearch(''); setViewPhraseFilters([]); } setPhraseFavsOnly(!phraseFavsOnly); } }} className={`p-2 rounded-lg transition-all ${(activeTab === 'REPORTS' ? reportFavsOnly : phraseFavsOnly) ? 'text-yellow-500 bg-yellow-50/10' : 'text-gray-400 hover:bg-slate-100'}`} title="Favoritos"><Heart size={18} fill={(activeTab === 'REPORTS' ? reportFavsOnly : phraseFavsOnly) ? "currentColor" : "none"}/></button>
                  <button 
                    onClick={() => activeTab === 'REPORTS' ? setAutoClearReports(!autoClearReports) : setAutoClearPhrases(!autoClearPhrases)} 
                    className={`p-2 rounded-lg transition-all ${(activeTab === 'REPORTS' ? autoClearReports : autoClearPhrases) ? (isDarkMode ? 'text-blue-400 bg-blue-900/20' : 'text-blue-600 bg-blue-50') : 'text-gray-400 hover:bg-slate-100'}`} 
                    title="Borrador Automático (10s)"
                  >
                    <Eraser size={18} />
                  </button>
                </div>
                <div className="flex space-x-1">
                   <button onClick={() => { if (activeTab === 'REPORTS') setShowReportFilters(!showReportFilters); else setShowPhraseFilters(!showPhraseFilters); }} className={`p-2 rounded-lg transition-all ${(activeTab === 'REPORTS' ? showReportFilters : showPhraseFilters) ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-400 hover:bg-slate-100'}`} title="Ver Filtros">{(activeTab === 'REPORTS' ? showReportFilters : showPhraseFilters) ? <Eye size={18} /> : <EyeOff size={18} />}</button>
                   <button onClick={() => { setEditingCategoryType(activeTab === 'REPORTS' ? 'REPORT' : 'PHRASE'); setLocalView(ViewState.MANAGE_FILTERS); }} className="p-2 text-gray-400 hover:bg-slate-100 rounded-lg transition-all" title="Gestionar Categorías"><Settings size={18} /></button>
                   <button onClick={() => setLocalView(ViewState.RECYCLE_BIN)} className="p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-all" title="Papelera"><Trash2 size={18} /></button>
                   <button onClick={() => { resetForms(); setLocalView(activeTab === 'REPORTS' ? ViewState.CREATE_REPORT : ViewState.CREATE_PHRASE); }} className={`p-2 rounded-xl text-white transition-all shadow-lg active:scale-95 ${activeTab === 'REPORTS' ? 'bg-blue-600 shadow-blue-600/20' : 'bg-purple-600 shadow-purple-600/20'}`} title="Añadir"><Plus size={20} /></button>
                </div>
             </div>
             <div className="relative"><Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" /><input type="text" placeholder={activeTab === 'REPORTS' ? "Buscar informe..." : "Buscar frase..."} className={`w-full pl-11 pr-4 py-2.5 border rounded-2xl text-[13px] font-bold outline-none focus:ring-2 focus:ring-blue-500/10 transition-all ${isDarkMode ? 'bg-[#1e2024] border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`} value={activeTab === 'REPORTS' ? reportSearch : phraseSearch} onChange={e => activeTab === 'REPORTS' ? setReportSearch(e.target.value) : setPhraseSearch(e.target.value)} /></div>
        </div>
        <div 
          style={{ height: (activeTab === 'REPORTS' ? showReportFilters : showPhraseFilters) ? `${filterPanelHeight}px` : '0px' }}
          className={`px-4 border-b transition-all duration-300 overflow-y-auto custom-scrollbar ${(activeTab === 'REPORTS' ? showReportFilters : showPhraseFilters) ? 'opacity-100 py-2' : 'opacity-0 border-none py-0 overflow-hidden'}`}
        >
          <FilterAccordion 
            categories={categories.filter(c => c.type === (activeTab === 'REPORTS' ? 'REPORT' : 'PHRASE')).sort((a,b) => (a.order ?? 0) - (b.order ?? 0))} 
            selectedFilters={activeTab === 'REPORTS' ? viewReportFilters : viewPhraseFilters} 
            onToggleFilter={(fid, cid) => { 
              if (activeTab === 'REPORTS') { 
                setReportSearch('');
                const cat = categories.find(it => it.id === cid); 
                if (!cat) return; 
                const cFids = cat.filters.map(f => f.id); 
                setViewReportFilters(prev => { 
                  const others = prev.filter(id => !cFids.includes(id)); 
                  if (fid === 'ALL') return others; 
                  setReportFavsOnly(false);
                  return prev.includes(fid) ? others : [...others, fid]; 
                }); 
              } else { 
                setPhraseSearch('');
                if (fid === 'ALL') { 
                  setViewPhraseFilters([]); 
                } else { 
                  setPhraseFavsOnly(false);
                  setViewPhraseFilters(prev => prev.includes(fid) ? [] : [fid]); 
                } 
              } 
            }} 
            isVisible={(activeTab === 'REPORTS' ? showReportFilters : showPhraseFilters)} 
            isDarkMode={isDarkMode} 
          />
        </div>
        {(activeTab === 'REPORTS' ? showReportFilters : showPhraseFilters) && (
          <div 
            onMouseDown={handleResizeStart}
            className={`h-1 cursor-row-resize z-20 shrink-0 transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-blue-600' : 'bg-gray-200 hover:bg-blue-400'}`}
          />
        )}
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar transition-colors ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
          {activeTab === 'REPORTS' ? (
            filteredReports.map(report => (
              <ReportCard 
                key={report.id} 
                report={report} 
                categories={categories} 
                onEdit={() => { 
                  setEditingId(report.id); 
                  setAiTitle(report.title); 
                  setNewReportContent(report.content); 
                  setSelectedReportFilters(report.filterIds); 
                  setLocalView(ViewState.EDIT_REPORT); 
                }} 
                onDelete={() => { 
                  if(confirm("¿Enviar a papelera?")) moveReportToTrash(user!.uid, report.id); 
                }} 
                onFavoriteToggle={(id) => { 
                  if(user) toggleFavoriteReportCloud(id, report.isFavorite, user.uid); 
                }} 
                onShare={(r) => handleShareClick(r, 'REPORT')} 
                onIA={onSelectForModular1} 
                isDarkMode={isDarkMode} 
              />
            ))
          ) : (
            filteredPhrases.map(phrase => (
              <PhraseCard 
                key={phrase.id} 
                phrase={phrase} 
                categories={categories} 
                isAutoCopied={lastAutoCopiedId === phrase.id}
                onEdit={(p) => { 
                  setEditingId(p.id); 
                  setNewPhraseText(p.text); 
                  setSelectedPhraseFilters(p.filterIds); 
                  setLocalView(ViewState.EDIT_PHRASE); 
                }} 
                onDelete={(id) => { 
                  if(confirm("¿Enviar a papelera?")) movePhraseToTrash(user!.uid, id); 
                }} 
                onFavoriteToggle={(id) => { 
                  if(user) toggleFavoritePhraseCloud(id, phrase.isFavorite, user.uid); 
                }} 
                onShare={(p) => handleShareClick(p, 'PHRASE')} 
                onIA={(text) => onSelectForModular1({ 
                  id: 'temp', 
                  title: 'FRASE SELECCIONADA', 
                  content: text, 
                  filterIds: [], 
                  isFavorite: false, 
                  usageCount: 0, 
                  createdAt: Date.now(), 
                  updatedAt: Date.now() 
                })} 
                isDarkMode={isDarkMode} 
              />
            ))
          )}
        </div>
    </div>
  );

  return (
    <div ref={containerRef} className="h-full w-full">
      {(() => {
        switch (localView) {
          case ViewState.CREATE_REPORT:
          case ViewState.EDIT_REPORT:
            return renderCreateIA();
          case ViewState.CREATE_PHRASE:
          case ViewState.EDIT_PHRASE:
            return renderCreatePhrase();
          case ViewState.MANAGE_FILTERS:
            return renderManageCategories();
          case ViewState.RECYCLE_BIN:
            return renderRecycleBin();
          default:
            return renderMainLibrary();
        }
      })()}

      <Modal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} title="COMPARTIR CON COLEGAS">
         <div className="space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2">Selecciona un radiólogo de la red</p>
            {directory.length === 0 ? (
              <div className="py-10 text-center text-gray-400 opacity-40">
                 <Users size={32} className="mx-auto mb-2" />
                 <p className="text-[10px] font-black uppercase tracking-widest">Sin contactos disponibles</p>
              </div>
            ) : (
              directory.map(u => (
                <button key={u.uid} onClick={() => executeShare(u)} disabled={isSharing} className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white hover:bg-indigo-50 border-gray-100 shadow-sm'}`}>
                   <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs shrink-0 ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{u.photoURL ? <img src={u.photoURL} className="w-full h-full rounded-full object-cover" alt="" /> : u.displayName.substring(0,2).toUpperCase()}</div>
                   <div className="min-w-0 flex-1">
                      <p className={`text-sm font-black truncate uppercase ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{u.displayName}</p>
                      <p className={`text-[9px] font-bold truncate uppercase text-gray-400`}>{u.mainSpecialty || 'Radiólogo'}</p>
                   </div>
                   {isSharing ? <RefreshCw size={14} className="animate-spin text-gray-400" /> : <ChevronRight size={18} className="text-gray-300" />}
                </button>
              ))
            )}
         </div>
      </Modal>
    </div>
  );
};

export default PruebaScreen;